import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Table, TableBody, TableContainer, TableRow, IconButton } from '@mui/material';
import { DeleteOutlineRounded as DeleteIcon } from '@mui/icons-material';
import { StyledTableCell, TablePaginationContainer } from '../styles';
import { ConfirmationDialog } from '@/components/shared/ConfirmationDialog';
import {
  GetAllTeamsForUserManagementDocument,
  GetAllTeamsForUserManagementQuery,
  GetUsersByTeamDocument,
  GetUsersByTeamQuery,
  User as UserType,
  useGetUsersByTeamLazyQuery,
  useRemoveUsersFromTeamMutation,
} from '@/__generated__/graphql';
import { Order, UsersTableHeadCell } from '@/types';
import { UsersTableHead } from '../shared/UsersTableHead';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { UserAvatarCell } from '../shared/UserAvatarCell';
import { TableSkeleton } from '../shared/TableSkeleton';
import { TableDataNotFound } from '../shared/TableDataNotFound';
import { TablePagination } from '../../shared/TablePagination';
import { defaultPaginationSize } from '@/constants';

type User = GetUsersByTeamQuery['usersByTeam'][0];
type Props = { selectedTeam: GetAllTeamsForUserManagementQuery['allTeams'][0] | undefined };

const headCells: UsersTableHeadCell[] = [
  {
    id: 'name',
    numeric: false,
    disablePadding: true,
    label: 'User Name',
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'Email Address',
  },
];

export const TeamUsers = ({ selectedTeam }: Props) => {
  const [deleteUserDialog, setDeleteUserDialog] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof UserType>('name');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteUser, setDeleteUser] = useState<User | undefined>(undefined);

  const [removeUsersFromTeam] = useRemoveUsersFromTeamMutation();
  const [getTeamUsers, { data, loading }] = useGetUsersByTeamLazyQuery();
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const { selectedTeamId, totalTeamUsers } = useMemo(() => {
    return {
      selectedTeamId: selectedTeam?.id,
      totalTeamUsers: selectedTeam?.users.length || 0,
    };
  }, [selectedTeam]);

  const currentTeamUsers = useMemo(() => data?.usersByTeam || [], [data]);

  useEffect(() => {
    if (selectedTeamId) {
      getTeamUsers({
        variables: {
          teamId: selectedTeamId,
          pageInfo: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
            sortBy: orderBy,
            sortDir: order === 'asc' ? 1 : -1,
          },
        },
      });
    }
  }, [getTeamUsers, order, orderBy, page, rowsPerPage, selectedTeamId]);

  const handleRequestSort = (headerId: keyof UserType) => {
    const isAsc = orderBy === headerId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(headerId);
  };

  const handleChangePage = (newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleCloseDialog = () => {
    setDeleteUserDialog(false);
  };

  const handleDeleteDialog = (user: User) => {
    setDeleteUser(user);
    setDeleteUserDialog(true);
  };

  const handleDeleteUserFromTeam = useCallback(() => {
    if (selectedTeamId && deleteUser) {
      removeUsersFromTeam({
        variables: {
          input: {
            teamId: selectedTeamId,
            userIds: [deleteUser.id],
          },
        },
        refetchQueries: [GetAllTeamsForUserManagementDocument, GetUsersByTeamDocument],
        onCompleted: (response) => {
          if (response) {
            showSnackBar(`User has been deleted successfully`, 'success');
          }
        },
      });
    }

    if (currentTeamUsers.length === 1 && page > 0) {
      setPage(page - 1);
    }
  }, [
    selectedTeamId,
    deleteUser,
    currentTeamUsers.length,
    page,
    removeUsersFromTeam,
    showSnackBar,
  ]);

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer sx={{ overflowY: 'auto' }}>
        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
          <UsersTableHead
            order={order}
            orderBy={orderBy}
            headCells={headCells}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {loading && <TableSkeleton />}
            {!loading &&
              currentTeamUsers.map((user) => {
                return (
                  <TableRow key={user.id} hover tabIndex={-1}>
                    <StyledTableCell>
                      <UserAvatarCell name={user?.name} picture={user?.picture} />
                    </StyledTableCell>
                    <StyledTableCell sx={{ width: '70%' }}>{user.email}</StyledTableCell>
                    <StyledTableCell sx={{ width: 72 }}>
                      <IconButton onClick={() => handleDeleteDialog(user)}>
                        <DeleteIcon color="primary" />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && selectedTeamId && currentTeamUsers.length === 0 && (
        <TableDataNotFound message="No Users Found" />
      )}
      {!loading && currentTeamUsers.length > 0 && (
        <TablePaginationContainer>
          <TablePagination
            count={totalTeamUsers}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[...defaultPaginationSize, { label: 'All', value: totalTeamUsers }]}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TablePaginationContainer>
      )}
      {deleteUserDialog && (
        <ConfirmationDialog
          isOpen={deleteUserDialog}
          title="Delete User From Team"
          description={`Are you sure you want to delete ${deleteUser?.name} from the team?`}
          confirmButtonText="Delete User From Team"
          cancelButtonText="Cancel"
          onConfirm={handleDeleteUserFromTeam}
          onCancel={handleCloseDialog}
          handleClose={handleCloseDialog}
        />
      )}
    </Box>
  );
};
